import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import TemplatePreview from "../templatePreview/TemplatePreview";
import { useTranslator } from "context/translator";
import { uploadPublicFileToAzure } from "utils/azure/azure";
import { useSelector } from "react-redux";

const EmailEditor = ({
  template,
  headerTemplates,
  footerTemplates,
  setTemplate,
}) => {
  const { translate } = useTranslator();
  const { user } = useSelector((state) => state.user);

  const [html, setHtml] = useState(template?.html || "");
  const quillRef = useRef(null);

  const handleImageUpload = useCallback(() => {
    const input = document.createElement("input");

    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];

      if (!file) return;

      try {
        const quill = quillRef.current?.getEditor();

        if (!quill) return;

        quill.focus();

        let range = quill.getSelection();

        if (!range) {
          range = {
            index: quill.getLength(),
            length: 0,
          };
        }

        const imageUrl = await uploadPublicFileToAzure(file, user);

        quill.insertEmbed(range.index, "image", imageUrl);
        quill.setSelection(range.index + 1);
      } catch (err) {
        console.error("Error uploading image:", err);
      }
    };
  }, [user]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline"],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          image: handleImageUpload,
        },
      },
    }),
    [handleImageUpload],
  );

  const formats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "color",
      "background",
      "align",
      "list",
      "bullet",
      "link",
      "image",
    ],
    [],
  );

  const handleOnChange = (value) => {
    setHtml(value || "");
    setTemplate((prev) => ({
      ...prev,
      html: value || "",
    }));
  };

  useEffect(() => {
    setHtml(template?.html || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  // =========================
  // TEMPLATE COMBINATION
  // =========================

  const headerTemplate =
    headerTemplates.find((t) => t._id === template?.headerTemplateId)?.body ||
    "";

  const footerTemplate =
    footerTemplates.find((t) => t._id === template?.footerTemplateId)?.body ||
    "";

  /**
   * IMPORTANT:
   * Header must OPEN html/body
   * Footer must CLOSE html/body
   */

  const finalHtml = `
    ${headerTemplate}
    ${html}
    ${footerTemplate}
  `;

  return (
    <div className="flex flex-col w-full h-full">
      <div className="flex justify-between">
        <div>
          <p>
            {translate("available-variables")}:
            <ul className="list-disc list-inside mb-2">
              <li>
                <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">
                  {translate("customer-name")} - {`{customerName}`}
                </code>
              </li>

              <li>
                <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">
                  {translate("customer-last-name")} - {`{customerLastName}`}
                </code>
              </li>

              <li>
                <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">
                  {translate("customer-full-name")} - {`{customerFullName}`}
                </code>
              </li>

              <li>
                <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">
                  {translate("customer-account-number")} -{" "}
                  {`{customerAccountNumber}`}
                </code>
              </li>

              <li>
                <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">
                  {translate("user-name")} - {`{userName}`}
                </code>
              </li>

              <li>
                <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">
                  {translate("user-last-name")} - {`{userLastName}`}
                </code>
              </li>

              <li>
                <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">
                  {translate("company-name")} - {`{companyName }`}
                </code>
              </li>
            </ul>
          </p>
        </div>
      </div>

      <p className="dark:text-white text-black mb-2">
        {translate("email-body")}
      </p>

      <ReactQuill
        ref={quillRef}
        value={html}
        onChange={handleOnChange}
        modules={modules}
        formats={formats}
        theme="snow"
        className="bg-white dark:bg-transparent dark:text-white quill-dark"
      />

      <br />
      <br />
      <br />

      <h2 className="text-lg font-semibold mt-6">{translate("preview")}</h2>

      <TemplatePreview
        html={finalHtml}
        isSms={false}
        key={"preview-email-" + (template?._id || "new")}
      />
    </div>
  );
};

export default EmailEditor;
